import { PublicCollectionItems } from "@/config";
import { type OnlyPublicProperties } from "@/types";
import { getEnumKeyByValue, isStringInEnum, getUrlParamAsBool, getUrlQueryAsString, getUrlQueryAsNumber, forceAscRangeOrder } from "@/utilities";
import { InjectionKey, Ref } from "vue";
import { CategoryId, CategorySelect } from "@/services/collection/categories";
import { router } from "@/router";
import { useUserSettings } from "@/composables/user";

export const filterInjectionKey = Symbol() as InjectionKey<Ref<OnlyPublicProperties<Filter>>>;

export enum SortBy {
  MarketplaceListedNewestFirst = "marketplaceListedNewestFirst",
  MarketplaceListedOldestFirst = "marketplaceListedOldestFirst",
  PublishedNewestFirst = "publishedNewestFirst",
  PublishedOldestFirst = "publishedOldestFirst",
  PriceMinToMax = "priceMinToMax",
  PriceMaxToMin = "priceMaxToMin",
  Relevance = "relevance",
  Likes = "likesCountDesc",
}

const DefaultSort = SortBy.PublishedNewestFirst;
const DefaultPage = 0;

export type CategoryFilterValue = CategoryId | typeof CategorySelect.ValueAllCategories | typeof CategorySelect.ValueFavouriteCategories;

type PriceLimit = number | undefined;

export type FilterDefaults = {
  fulltext: string;
  categoryId: CategoryFilterValue;
  minPrice: PriceLimit;
  maxPrice: PriceLimit;
  onlyForSale: boolean;
  sortOrder: SortBy;
  page: number;
  perPage: number;
  includeNegotiablePrice: boolean;
  excludeMy: boolean;
  nickname: string | undefined;
};

export class Filter {
  private _fulltext: string;
  private _categoryId: CategoryFilterValue = CategorySelect.ValueAllCategories;
  private _minPrice: PriceLimit;
  private _maxPrice: PriceLimit;
  private _onlyForSale: boolean;
  private _sortOrder: SortBy;
  private _page = DefaultPage;
  private _perPage = PublicCollectionItems.listing.perPage;
  private _includeNegotiablePrice = true;
  private _excludeMy = false;
  private _nickname: string | undefined;

  private defaults: FilterDefaults;
  private emptyState: FilterDefaults;

  private _activeCount = 0;

  constructor(customDefaults: Partial<FilterDefaults> = {}, emptyState: Partial<FilterDefaults> = {}) {
    this.defaults = {
      fulltext: "",
      categoryId: CategorySelect.ValueAllCategories,
      minPrice: undefined,
      maxPrice: undefined,
      onlyForSale: false,
      sortOrder: DefaultSort,
      page: DefaultPage,
      perPage: PublicCollectionItems.listing.perPage,
      includeNegotiablePrice: true,
      nickname: undefined,
      excludeMy: false,
      ...customDefaults,
    };

    const receivedEmptyStateWithoutUndefined = Object.fromEntries(Object.entries(emptyState).filter(([_, value]) => value !== undefined));
    this.emptyState = {
      fulltext: "",
      categoryId: CategorySelect.ValueAllCategories,
      minPrice: this.defaults.minPrice,
      maxPrice: this.defaults.maxPrice,
      onlyForSale: false,
      sortOrder: DefaultSort,
      page: 0,
      perPage: PublicCollectionItems.listing.perPage,
      includeNegotiablePrice: true,
      nickname: undefined,
      excludeMy: false,
      ...receivedEmptyStateWithoutUndefined,
    };

    this.reset();
  }

  public fillFromUrl(): Filter {
    this.reset();

    try {
      this.fulltext = getUrlQueryAsString("fulltext");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.categoryId = getUrlQueryAsNumber("categoryId");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.minPrice = getUrlQueryAsNumber("minPrice");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.maxPrice = getUrlQueryAsNumber("maxPrice");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.onlyForSale = getUrlParamAsBool("onlyForSale", true);
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.includeNegotiablePrice = getUrlParamAsBool("includeNegotiablePrice", true);
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.excludeMy = getUrlParamAsBool("excludeMy", true);
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      const sort = getUrlQueryAsString("sortOrder");
      const isValid = isStringInEnum(sort, SortBy);
      if (isValid) {
        this.sortOrder = getEnumKeyByValue(sort, SortBy);
      }
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.page = getUrlQueryAsNumber("page");
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    return this;
  }

  public reset(): void {
    this.fulltext = this.defaults.fulltext;
    this.categoryId = this.defaults.categoryId;
    this.minPrice = this.defaults.minPrice;
    this.maxPrice = this.defaults.maxPrice;
    this.onlyForSale = this.defaults.onlyForSale;
    this.sortOrder = this.defaults.sortOrder;
    this.page = this.defaults.page;
    this._perPage = this.defaults.perPage;
    this.includeNegotiablePrice = this.defaults.includeNegotiablePrice;
    this.excludeMy = this.defaults.excludeMy;
    this._nickname = this.defaults.nickname;
  }

  public isSelectedFavouriteCategories(): boolean {
    return this.categoryId === CategorySelect.ValueFavouriteCategories;
  }

  public isSomeSpecificCategorySelected(): boolean {
    return this.categoryId !== CategorySelect.ValueAllCategories && this.categoryId !== CategorySelect.ValueFavouriteCategories;
  }

  public isSomethingSelectedExceptCategory(): boolean {
    return (
      this.fulltext !== "" || //
      this.minPrice !== undefined ||
      this.maxPrice !== undefined ||
      this.onlyForSale !== true ||
      this.includeNegotiablePrice === false ||
      this.excludeMy === true
    );
  }

  /** Active filters counting */
  public get activeCount(): number {
    return this._activeCount;
  }

  private recountActiveFilters(): void {
    let c = 0;
    if (this.fulltext !== this.emptyState.fulltext) {
      c++;
    }
    if (this.categoryId !== this.emptyState.categoryId) {
      c++;
    }
    if (this.minPrice !== this.emptyState.minPrice || this.maxPrice !== this.emptyState.maxPrice || this.includeNegotiablePrice !== this.emptyState.includeNegotiablePrice) {
      c++;
    }
    if (this.onlyForSale !== this.emptyState.onlyForSale) {
      c++;
    }
    this._activeCount = c;
  }

  /** Filter params setters / getters */

  public set fulltext(newValue: string) {
    this._fulltext = newValue;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get fulltext(): string {
    return this._fulltext;
  }

  public set categoryId(newCategoryId: CategoryFilterValue) {
    this._categoryId = newCategoryId;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get categoryId(): CategoryFilterValue {
    return this._categoryId;
  }

  public set sortOrder(newSortOrder: SortBy) {
    this._sortOrder = newSortOrder;
    this.page = this.defaults.page;
  }

  public get sortOrder(): SortBy {
    return this._sortOrder;
  }

  public set page(newPage: number) {
    this._page = newPage;
  }

  public get page(): number {
    return this._page;
  }

  public set perPage(newPerPage: number) {
    this._perPage = newPerPage;
  }

  public get perPage(): number {
    return this._perPage;
  }

  public set minPrice(newMinPrice: PriceLimit) {
    this._minPrice = newMinPrice;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get minPrice(): PriceLimit {
    return this._minPrice;
  }

  public set maxPrice(newMaxPrice: PriceLimit) {
    this._maxPrice = newMaxPrice;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get maxPrice(): PriceLimit {
    return this._maxPrice;
  }

  public set onlyForSale(onlyForSale: boolean) {
    this._onlyForSale = onlyForSale;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get onlyForSale(): boolean {
    return this._onlyForSale;
  }

  public set includeNegotiablePrice(include: boolean) {
    this._includeNegotiablePrice = include;
    this.page = this.defaults.page;
    this.recountActiveFilters();
  }

  public get includeNegotiablePrice(): boolean {
    return this._includeNegotiablePrice;
  }

  public set excludeMy(exclude: boolean) {
    this._excludeMy = exclude;
    this.page = this.defaults.page;
  }

  public get excludeMy(): boolean {
    return this._excludeMy;
  }

  public get nickname(): string | undefined {
    return this._nickname;
  }
}

export function createApiParams(filter: OnlyPublicProperties<Filter>): Record<string, number | CategoryId | CategoryId[] | string | boolean | undefined> {
  const q = filter.fulltext;
  const fulltext = q === undefined || q === "" ? undefined : q;
  const [minPrice, maxPrice] = forceAscRangeOrder(filter.minPrice, filter.maxPrice);

  const params = <Record<string, number | CategoryId[] | string | boolean | undefined>>{
    fulltext,
    minPrice,
    maxPrice,
    onlyForSale: filter.onlyForSale ? 1 : 0,
    includeNegotiablePrice: filter.includeNegotiablePrice ? 1 : 0,
    sortOrder: filter.sortOrder,
    page: filter.page,
    perPage: filter.perPage,
    excludeMy: filter.excludeMy ? 1 : 0,
    nickname: filter.nickname,
  };

  if (filter.isSelectedFavouriteCategories()) {
    const { userSettings } = useUserSettings();
    if (userSettings.value === undefined) {
      throw new Error("userSettings is undefined");
    }
    params["categoryId"] = userSettings.value?.favoriteCategoryIds;
  } else {
    params["categoryId"] = filter.categoryId === CategorySelect.ValueAllCategories ? undefined : filter.categoryId;
  }

  return params;
}

export function createUrlParams(filter: OnlyPublicProperties<Filter>) {
  const q = filter.fulltext;
  const fulltext = q === undefined || q === "" ? undefined : q;
  const [minPrice, maxPrice] = forceAscRangeOrder(filter.minPrice, filter.maxPrice);

  const params = <Record<string, number | CategoryFilterValue | string | undefined>>{
    categoryId: filter.categoryId,
    fulltext,
    minPrice,
    maxPrice,
    onlyForSale: filter.onlyForSale ? 1 : 0,
    includeNegotiablePrice: filter.includeNegotiablePrice ? 1 : 0,
    sortOrder: filter.sortOrder,
    page: filter.page,
    perPage: filter.perPage,
  };

  return params;
}

export function updateCurrentUrl(filter: OnlyPublicProperties<Filter>) {
  const params = createUrlParams(filter);
  router.replace({
    path: router.currentRoute.value.path,
    query: params,
  });
}
